import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { styled } from '@mui/system';
import { useLocation } from 'react-router-dom';
import { ReactComponent as CetraLogo } from '../../assets/icons/CetraLogo.svg';
import { ReactComponent as LogoCircle } from '../../assets/icons/LogoCircle.svg';
import ClinicIcon from '../../assets/icons/Clinic.svg';
import subscriptionPlans from '../authenticated/settings/subscriptionPlans';
import SettingsBilling from '../authenticated/settings/settings-billing';

const PLAN_DISPLAY_NAMES = {
    Free: 'Free',
    Professional: 'Pro',
    Clinic: 'Clinic',
};

const PLAN_ICONS = {
    Free: LockOpenOutlinedIcon,
    Professional: WorkOutlineIcon,
};

const StyledToggleButton = styled(Button)(({ selected }) => ({
    flex: 1,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    borderRadius: '50px',
    padding: '8px 24px',
    border: 'none',
    color: selected ? '#fff' : '#000453',
    backgroundColor: selected ? '#000453' : 'transparent',
    '&:hover': {
        backgroundColor: selected ? '#000453' : 'rgba(0,4,83,0.06)',
    },
    minWidth: 100,
    lineHeight: 1.5,
}));

const Checkout = () => {
    const location = useLocation();
    const { plan = 'Professional', billingCycle: initialBillingCycle = 'monthly' } = location.state || {};
    const [billingCycle, setBillingCycle] = useState(initialBillingCycle);
    const selectedPlan = subscriptionPlans[plan] || subscriptionPlans.Professional;
    const [promoDiscount, setPromoDiscount] = useState(0);
    const [nextPaymentDate, setNextPaymentDate] = useState('');
    const isShowOffer = localStorage.getItem('LVS') === 'true';

    // ProVet params (kept for potential future use in billing; not required here)
    // const provetClientId = localStorage.getItem('provet_client');
    // const provetUserId = localStorage.getItem('provet_user');
    // const provetTimestamp = localStorage.getItem('provet_timestamp');
    // const provetVerify = localStorage.getItem('provet_verify');

    // Redirect if Free plan somehow reaches checkout
    useEffect(() => {
        if (plan === 'Free') {
            console.warn('Free plan redirected from checkout');
            window.location.href = '/';
        }
    }, [plan]);

    const [planDetails, setPlanDetails] = useState({
        priceId: billingCycle === 'monthly' ? selectedPlan.priceMonthlyId : selectedPlan.priceAnnuallyId,
        price: billingCycle === 'monthly' ? selectedPlan.priceMonthly : selectedPlan.priceAnnually,
        productId: selectedPlan.productId,
        isMonthly: billingCycle === 'monthly',
        accountType: selectedPlan.accountType,
    });

    useEffect(() => {
        setPlanDetails({
            priceId: billingCycle === 'monthly' ? selectedPlan.priceMonthlyId : selectedPlan.priceAnnuallyId,
            price: billingCycle === 'monthly' ? selectedPlan.priceMonthly : selectedPlan.priceAnnually,
            productId: selectedPlan.productId,
            isMonthly: billingCycle === 'monthly',
            accountType: selectedPlan.accountType,
            couponId: selectedPlan.couponId,
        });

        const today = new Date();
        const nextDate = new Date(today);
        if (billingCycle === 'monthly') {
            nextDate.setMonth(today.getMonth() + 1);
        } else {
            nextDate.setFullYear(today.getFullYear() + 1);
        }
        setNextPaymentDate(nextDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
    }, [billingCycle, selectedPlan]);

    const basePrice = billingCycle === 'monthly' ? selectedPlan.priceMonthly : selectedPlan.priceAnnually;
    const discountedPrice = promoDiscount?.type === 'percent'
        ? Math.max(0, basePrice - basePrice * (promoDiscount.value / 100))
        : promoDiscount?.type === 'amount'
            ? Math.max(0, basePrice - promoDiscount.value)
            : null;
    const displayPrice = basePrice.toFixed(2);

    const PlanIcon = PLAN_ICONS[plan] || LockOpenOutlinedIcon;
    const displayName = PLAN_DISPLAY_NAMES[plan] || plan;
    const isClinicPlan = plan === 'Clinic';

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#F0F0FC',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: { xs: 4, md: 6 },
                px: { xs: 2, md: 3 },
            }}
        >
            {/* Logo */}
            <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Box sx={{ position: 'relative', width: { xs: 50, md: 64 }, height: { xs: 50, md: 64 } }}>
                    <Box
                        component={LogoCircle}
                        sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                    <Box
                        component={CetraLogo}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: 29, md: 42 },
                            height: { xs: 22, md: 32 },
                        }}
                    />
                </Box>
            </Box>

            {/* Plan overview card */}
            <Box
                sx={{
                    backgroundColor: '#fff',
                    borderRadius: '30px',
                    border: '1px solid rgba(102, 102, 102, 0.15)',
                    width: '100%',
                    maxWidth: 646,
                    p: { xs: '24px', md: '40px 30px 30px' },
                    mb: 2,
                }}
            >
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '26px', md: '36px' },
                            fontWeight: 600,
                            color: '#000453',
                            textAlign: 'center',
                            letterSpacing: { xs: '-0.52px', md: '-0.72px' },
                            lineHeight: { xs: '26px', md: '36px' },
                            mb: { xs: '8px', md: '20px' },
                        }}
                    >
                        Overview of your plan
                    </Typography>
                    <Typography
                        sx={{
                            color: '#616063',
                            textAlign: 'center',
                            fontSize: { xs: '12px', md: '16px' },
                            fontWeight: 500,
                            lineHeight: { xs: '20px', md: '16px' },
                            letterSpacing: { xs: '-0.24px', md: '-0.32px' },
                            mb: { xs: 2, md: '28px' },
                        }}
                    >
                        {displayName} Plan – First payment today, next payment<br />on {nextPaymentDate}.
                    </Typography>

                    {/* Billing toggle */}
                    {!isShowOffer && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e5e5e5',
                                borderRadius: '58px',
                                p: '4px',
                                gap: '4px',
                                height: '44px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <StyledToggleButton
                                selected={billingCycle === 'monthly' ? 1 : 0}
                                onClick={() => setBillingCycle('monthly')}
                            >
                                Monthly
                            </StyledToggleButton>
                            <StyledToggleButton
                                selected={billingCycle === 'annually' ? 1 : 0}
                                onClick={() => setBillingCycle('annually')}
                            >
                                Annual
                            </StyledToggleButton>
                        </Box>
                    )}
                </Box>

                {/* Mini plan card — 56px from each edge of the overview card (30px parent padding + 26px margin) */}
                <Box
                    sx={{
                        borderRadius: '30px',
                        border: '0.5px solid #e5e5e5',
                        overflow: 'hidden',
                        mx: { xs: 0, md: '26px' },
                    }}
                >
                    {/* Gray top section */}
                    <Box
                        sx={{
                            backgroundColor: '#f0f0fc',
                            p: 3,
                        }}
                    >
                        {/* Icon */}
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#fff',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1.5,
                            }}
                        >
                            {isClinicPlan ? (
                                <Box component="img" src={ClinicIcon} alt="Clinic" sx={{ width: 20, height: 22 }} />
                            ) : (
                                <PlanIcon sx={{ color: '#FF669B', fontSize: 22 }} />
                            )}
                        </Box>

                        {/* Plan name */}
                        <Typography
                            sx={{
                                fontSize: { xs: '32px', md: '30px' },
                                fontWeight: 600,
                                color: '#000453',
                                letterSpacing: { xs: '-1.28px', md: '-0.6px' },
                                lineHeight: { xs: '32px', md: 1.1 },
                                mb: 0.5,
                            }}
                        >
                            {displayName}
                        </Typography>

                        {/* Description */}
                        <Typography sx={{ fontSize: { xs: '12px', md: '16px' }, color: '#616063', lineHeight: { xs: '16px', md: '20px' }, letterSpacing: { xs: '-0.24px', md: '-0.32px' }, mb: 1.5 }}>
                            {selectedPlan.description}
                        </Typography>

                        {/* Price */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            {discountedPrice !== null && (
                                <Typography
                                    sx={{
                                        fontSize: { xs: '18px', md: '20px' },
                                        fontWeight: 600,
                                        color: '#616063',
                                        letterSpacing: '-0.56px',
                                        textDecoration: 'line-through',
                                    }}
                                >
                                    £{displayPrice}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontSize: { xs: '24px', md: '28px' },
                                    fontWeight: 600,
                                    color: discountedPrice !== null ? '#FF669B' : '#000453',
                                    letterSpacing: '-0.56px',
                                }}
                            >
                                £{discountedPrice !== null ? discountedPrice.toFixed(2) : displayPrice}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: '#616063', fontWeight: 400 }}>
                                {billingCycle === 'annually' ? '/year' : '/month'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* White bottom section — benefits */}
                    <Box sx={{ backgroundColor: '#fff', p: 3 }}>
                        {(selectedPlan.benefits || []).map((benefit, idx) => {
                            const isBold = benefit.startsWith('**') && benefit.endsWith('**');
                            const text = isBold ? benefit.slice(2, -2) : benefit;
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        mb: 1,
                                    }}
                                >
                                    <CheckCircleOutlineIcon
                                        sx={{ color: '#FF669B', fontSize: 16, mt: '2px', flexShrink: 0 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '12px', md: '13px' },
                                            color: '#616063',
                                            lineHeight: { xs: '12px', md: 1.5 },
                                            letterSpacing: '-0.24px',
                                            fontWeight: isBold ? 700 : 400,
                                        }}
                                    >
                                        {text}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Billing form — contains card fields, promo code, Go back + Continue + confirmation dialog */}
            <Box sx={{ width: '100%', maxWidth: 646 }}>
                <SettingsBilling
                    plan={planDetails}
                    editorActiveDefault={true}
                    onPromoDiscountChange={setPromoDiscount}
                    showPromo={true}
                    isShowOffer={isShowOffer && billingCycle === 'annually'}
                    isOnboarding={true}
                />
            </Box>
        </Box>
    );
};

export default Checkout;
